export type WebClientOS = 'win32' | 'darwin' | 'linux';
export type WebClientArch = 'x64' | 'arm64' | 'ia32';

export interface WebClientRuntimeSource {
  userAgent?: string;
  platform?: string;
  language?: string;
}

export interface WebClientRuntime {
  os: WebClientOS;
  osVersion: string;
  arch: WebClientArch;
  locale: string;
  browserName: string;
  browserVersion: string;
}

/** detectBrowser 从标准User-Agent识别浏览器名称与主版本信息。 */
function detectBrowser(userAgent: string): { browserName: string; browserVersion: string } {
  const candidates: Array<[RegExp, string]> = [
    [/Edg\/([\d.]+)/i, 'Edge'],
    [/OPR\/([\d.]+)/i, 'Opera'],
    [/Chrome\/([\d.]+)/i, 'Chrome'],
    [/Firefox\/([\d.]+)/i, 'Firefox'],
    [/Version\/([\d.]+).*Safari\//i, 'Safari'],
  ];
  for (const [pattern, browserName] of candidates) {
    const matched = userAgent.match(pattern);
    if (matched?.[1]) return { browserName, browserVersion: matched[1] };
  }
  return { browserName: '', browserVersion: '' };
}

/** detectWebClientRuntime 从同一浏览器身份来源解析系统与版本，避免兼容字段互相矛盾。 */
export function detectWebClientRuntime(source: WebClientRuntimeSource): WebClientRuntime {
  const userAgent = source.userAgent?.trim() ?? '';
  const platform = source.platform?.trim() ?? '';
  const os: WebClientOS = /Windows NT/i.test(userAgent)
    ? 'win32'
    : /Macintosh|Mac OS X/i.test(userAgent)
      ? 'darwin'
      : /Linux|Android/i.test(userAgent)
        ? 'linux'
        : /windows|win32/i.test(platform)
          ? 'win32'
          : /macintosh|macintel|mac os|darwin/i.test(platform)
            ? 'darwin'
            : 'linux';
  const osVersion = os === 'win32'
    ? userAgent.match(/Windows NT\s+([\d.]+)/i)?.[1] ?? ''
    : os === 'darwin'
      ? (userAgent.match(/Mac OS X\s+([\d_]+)/i)?.[1] ?? '').replace(/_/g, '.')
      : userAgent.match(/(?:Linux|Android)\s+([^;)]+)/i)?.[1]?.trim() ?? '';
  const architectureSource = `${userAgent} ${platform}`;
  const arch: WebClientArch = /arm64|aarch64|apple silicon/i.test(architectureSource)
    ? 'arm64'
    : /(?:i[3-6]86|x86)(?!_64)/i.test(architectureSource)
      ? 'ia32'
      : 'x64';
  const browser = detectBrowser(userAgent);

  return {
    os,
    osVersion,
    arch,
    locale: source.language?.trim() ?? '',
    ...browser,
  };
}
